import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { GroupContextService } from './group-context.service';

export interface IRequestOptions {
  headers?: HttpHeaders;
  observe?: any;
  params?:
    | HttpParams
    | {
        [param: string]: string | string[];
      };
  reportProgress?: boolean;
  responseType?: 'json';
  withCredentials?: boolean;
  body?: any;
}

export interface PermissionError {
  permission: string;
}

export function isPermissionError(error: any): error is PermissionError {
  return error?.permission;
}

/** Wraps Angular's HttpClient to provide additional application-specific functionality */
@Injectable({
  providedIn: 'root',
})
export class HttpService {
  static readonly GROUP_HEADER_ID = 'hd-group-id';
  static readonly MC_TOKENS_HEADER_ID = 'hd-mc-tokens';
  static readonly SNAPSHOT_FORMAT_HEADER_ID = 'hd-snapshot-format';
  static readonly TIMEZONE_OFFSET_HEADER_ID = 'hd-timezone-offset';

  /**
   * Appends the headers related to the display of snapshot (device info) data to the provided list of headers.
   * @param headers HttpHeaders to append to
   * @return A new HttpHeaders object with the snapshot display options appended
   */
  static addSnapshotDisplayOptionsHeaders(headers: HttpHeaders): HttpHeaders {
    const now = new Date();
    const timeZoneOffset = now.getTimezoneOffset().toString();

    headers = headers.append(HttpService.SNAPSHOT_FORMAT_HEADER_ID, 'Display');
    return headers.append(HttpService.TIMEZONE_OFFSET_HEADER_ID, timeZoneOffset);
  }

  constructor(private http: HttpClient, private groupContextService: GroupContextService) {}

  /**
   * Construct a GET request which interprets the body as JSON and returns it.
   *
   * @return an `Observable` of the body as type `T`.
   */
  get<T>(url: string, options?: IRequestOptions): Observable<T> {
    return this.http.get<T>(url, this.transformOptions(options));
  }

  /**
   * Construct a PUT request which interprets the body as JSON and returns it.
   *
   * @return an `Observable` of the body as type `T`.
   */
  put<T>(url: string, body: any | null, options?: IRequestOptions): Observable<T> {
    return this.http.put<T>(url, body, this.transformOptions(options));
  }

  /**
   * Construct a POST request which interprets the body as JSON and returns it.
   *
   * @return an `Observable` of the body as type `T`.
   */
  post<T>(url: string, body: any | null, options?: IRequestOptions): Observable<T> {
    return this.http.post<T>(url, body, this.transformOptions(options));
  }

  /**
   * Construct a DELETE request which interprets the body as JSON and returns it.
   *
   * @return an `Observable` of the body as type `T`.
   */
  delete<T>(url: string, options?: IRequestOptions): Observable<T> {
    return this.http.delete<T>(url, this.transformOptions(options));
  }

  private transformOptions(options?: IRequestOptions): IRequestOptions {
    // If there's a current group set on GroupContextService, add a custom hd-group-id header to this request
    const groupId = this.groupContextService.currentGroupId;
    if (groupId === -1) {
      // This prevents some unintended 403s but probably has a deeper cause.
      console.log('Treating groupId -1 as no selected group');
    } else if (groupId) {
      options = options || {};
      if (!options.headers) {
        options.headers = new HttpHeaders();
      }

      if (!options.headers.has(HttpService.GROUP_HEADER_ID)) {
        options.headers = options.headers.set(HttpService.GROUP_HEADER_ID, groupId.toString());
      }
    }

    return options;
  }
}
